import { Tooltip, TooltipProps, Typography } from 'antd';
import { TextProps } from 'antd/lib/typography/Text';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/tr';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useEffect, useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';

type Props = {
    textProps?: TextProps;
    time: number;
    formatType?: string;
    tooltipProps?: TooltipProps;
};

const styles = {
    tooltip: { width: 'max-content' }
};

function formatDate(
    dateInMilliseconds: number | Dayjs,
    {
        formatType = 'LLLL',
        fromNow = false,
        language
    }: {
        formatType?: string;
        fromNow?: boolean;
        language?: string;
    }
) {
    if (!dateInMilliseconds) return;
    dayjs.locale((language || getI18n().language).toLocaleLowerCase());
    if (typeof dateInMilliseconds === 'number') {
        if (fromNow) {
            dayjs.extend(relativeTime);
            return dayjs(dateInMilliseconds).fromNow();
        }

        dayjs.extend(localizedFormat);
        return dayjs(dateInMilliseconds).format(formatType);
    }
    return dateInMilliseconds.format(formatType);
}

export default function TimeDisplay({ textProps = {}, time, formatType, tooltipProps = {} }: Props) {
    const { t } = useTranslation('common');

    const [, setForcedUpdate] = useState({});

    useEffect(() => {
        // HACK: This is for changing time text over time
        const forcedUpdateControl = setInterval(() => {
            setForcedUpdate({});
        }, 60000);

        return () => {
            clearInterval(forcedUpdateControl);
        };
    }, []);

    const timeText = formatType
        ? formatDate(time, { formatType: formatType })
        : `${t('updatedAt', { dateText: formatDate(time, { fromNow: true }) })}`;

    return (
        <Tooltip placement="topRight" title={formatDate(time, {})} overlayInnerStyle={styles.tooltip} {...tooltipProps}>
            <Typography.Text type="secondary" {...textProps}>
                {timeText}
            </Typography.Text>
        </Tooltip>
    );
}

export { formatDate };
